import { DateTime } from 'luxon'

/**
 * The CRM's operating zone (crm-core `INTERNAL_TIMEZONE`). Reminder days,
 * "today" and the snooze presets are all read here, not in the viewer's
 * browser zone: a promised "tomorrow 09:00" is the customer's 09:00.
 */
export const REMINDER_TIMEZONE = 'Asia/Jakarta'

/** Server cap on a reminder note (crm-core `REMINDER_NOTE_TOO_LONG`). */
export const REMINDER_NOTE_MAX_LENGTH = 500

/** Hour of the "tomorrow morning" preset, in {@link REMINDER_TIMEZONE}. */
const TOMORROW_PRESET_HOUR = 9

export type ReminderPresetKey = 'plusOneHour' | 'plusThreeHours' | 'tomorrowMorning'

export interface ReminderPreset {
  key: ReminderPresetKey
  at: DateTime
}

const toJakarta = (now: DateTime): DateTime => now.setZone(REMINDER_TIMEZONE)

/** Tomorrow 09:00 in Jakarta, whatever zone `now` came in. */
export const tomorrowMorning = (now: DateTime): DateTime =>
  toJakarta(now)
    .plus({ days: 1 })
    .set({ hour: TOMORROW_PRESET_HOUR, millisecond: 0, minute: 0, second: 0 })

/**
 * The quick choices for a new reminder: +1 h, +3 h and tomorrow 09:00. The
 * relative ones are rounded down to the minute, so the chip and the saved
 * value agree.
 */
export const reminderCreatePresets = (now: DateTime): ReminderPreset[] => {
  const base = toJakarta(now).startOf('minute')
  return [
    { at: base.plus({ hours: 1 }), key: 'plusOneHour' },
    { at: base.plus({ hours: 3 }), key: 'plusThreeHours' },
    { at: tomorrowMorning(now), key: 'tomorrowMorning' },
  ]
}

/**
 * The snooze choices (spec §10): +1 h and tomorrow 09:00. "Pick…" is a dialog.
 *
 * "+1 h" counts from the later of now and `dueAt`, so snoozing a reminder that
 * is not due yet pushes it back an hour instead of pulling it in to an hour
 * from now.
 */
export const snoozePresets = (now: DateTime, dueAt?: string): ReminderPreset[] => {
  const due = dueAt ? DateTime.fromISO(dueAt) : null
  const base = due?.isValid && due.toMillis() > now.toMillis() ? due : now
  return [
    { at: toJakarta(base).startOf('minute').plus({ hours: 1 }), key: 'plusOneHour' },
    { at: tomorrowMorning(now), key: 'tomorrowMorning' },
  ]
}

/**
 * Due before `now`. The server sends `isOverdue` too; this is for a value the
 * client holds before any round trip (a form, a clock that moved on).
 */
export const isOverdue = (dueAt: string, now: DateTime = DateTime.now()): boolean =>
  DateTime.fromISO(dueAt).toMillis() < now.toMillis()

/** Start (inclusive) and end (exclusive) of the Jakarta day holding `at`, as ISO. */
export const jakartaDayBounds = (at: DateTime): { start: string; end: string } => {
  const day = toJakarta(at).startOf('day')
  return { end: day.plus({ days: 1 }).toISO()!, start: day.toISO()! }
}

/** The Jakarta calendar day (`YYYY-MM-DD`) a due time falls on. */
export const jakartaDayKey = (dueAt: string): string =>
  DateTime.fromISO(dueAt, { zone: REMINDER_TIMEZONE }).toISODate()!

/** Groups rows by their Jakarta day, keeping the input order inside each day. */
export const groupByJakartaDay = <T extends { dueAt: string }>(
  rows: readonly T[],
): Map<string, T[]> => {
  const groups = new Map<string, T[]>()
  for (const row of rows) {
    const key = jakartaDayKey(row.dueAt)
    groups.set(key, [...(groups.get(key) ?? []), row])
  }

  return groups
}

/**
 * The instant to send for a picked date-time. The picker works in
 * {@link REMINDER_TIMEZONE}; the ISO keeps its `+07:00` offset so the value
 * read back in a log is the time the operator chose.
 */
export const toReminderDueAt = (value: DateTime): string =>
  toJakarta(value).startOf('minute').toISO({ suppressMilliseconds: true })!

/** How a due time reads in a row or a line: weekday, day, month, 24-hour time. */
export const REMINDER_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  hour: '2-digit',
  hourCycle: 'h23',
  minute: '2-digit',
  month: 'short',
  weekday: 'short',
}

/** A due time in Jakarta, e.g. "Mon, 26 Oct, 10:00". Unparseable input is returned as is. */
export const formatReminderTime = (dueAt: string, locale: string): string => {
  const dt = DateTime.fromISO(dueAt, { zone: REMINDER_TIMEZONE }).setLocale(locale)
  return dt.isValid ? dt.toLocaleString(REMINDER_TIME_FORMAT) : dueAt
}
